import type { LocaleCode } from './index';
import { intlLocale } from './index';

/**
 * Locale-aware money and dates.
 *
 * The voucher card sets the currency symbol apart from the figure as a designed lockup, so
 * amounts are split rather than run through `Intl` wholesale — but the grouping, the decimal
 * separator and the date wording all follow the guest's locale.
 */

/** "26 August 2027" / "26 augustus 2027" / "26 de agosto de 2027". */
export function formatLongDate(value: string | Date | undefined, locale: LocaleCode): string {
  if (!value) return '';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  try {
    return new Intl.DateTimeFormat(intlLocale(locale), {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }).format(date);
  } catch {
    return date.toISOString().slice(0, 10);
  }
}

/** "€100.00" in English, "100,00 €" in French — symbol placement included. */
export function formatMoney(amount: number, currency: string, locale: LocaleCode): string {
  try {
    return new Intl.NumberFormat(intlLocale(locale), {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return `${currency} ${amount.toFixed(2)}`;
  }
}

/** A round price with no decimals, e.g. the preset tiles and the set-menu list. */
export function formatWhole(amount: number, locale: LocaleCode): string {
  try {
    return new Intl.NumberFormat(intlLocale(locale), { maximumFractionDigits: 0 }).format(amount);
  } catch {
    return String(Math.round(amount));
  }
}

export interface AmountParts {
  /** The integer part, grouped for the locale. */
  whole: string;
  /** The locale's decimal separator, present only when there are cents to show. */
  separator: string | null;
  /** Two digits, or null on a round amount. */
  cents: string | null;
}

/** Splits an amount for the card's big lockup without losing the locale's conventions. */
export function formatAmountParts(amount: number, locale: LocaleCode): AmountParts {
  const rounded = Math.round(amount * 100) / 100;
  const whole = Math.floor(rounded);
  const cents = Math.round((rounded - whole) * 100);
  return {
    whole: formatWhole(whole, locale),
    separator: cents > 0 ? decimalSeparator(locale) : null,
    cents: cents > 0 ? String(cents).padStart(2, '0') : null,
  };
}

function decimalSeparator(locale: LocaleCode): string {
  try {
    return (
      new Intl.NumberFormat(intlLocale(locale))
        .formatToParts(1.1)
        .find((part) => part.type === 'decimal')?.value || '.'
    );
  } catch {
    return '.';
  }
}
